/**
 * use-mobile-channels.ts — Thứ tự tài khoản kênh ở đầu Hộp thư điện thoại, LƯU THEO TÀI KHOẢN và đồng bộ giữa các thiết bị.
 *
 *   GET/PUT /me/ui-prefs/mobile-channels   { channels, updatedAt }   (backend: modules/me/ui-prefs*.ts, AppSetting `ui.mobile-channels.<userId>`)
 *
 * Cùng cơ chế với use-mobile-segments.ts / use-composer-tools.ts:
 * - localStorage `ecdp_mobile_channels:<userId>` là bộ nhớ đệm — hiện NGAY khi mở, không chớp chờ mạng; mỗi người dùng một khoá.
 * - Mở / quay lại tab: lấy bản máy chủ và hợp nhất (`mergeChannelsPref`): bản `updatedAt` mới hơn thắng; máy chủ chưa có mà máy này có → đẩy lên.
 * - Sửa: áp ngay + lưu đệm + PUT nền sau ~500ms (gộp nhiều lần sửa; rời màn thì gửi luôn). Lỗi mạng → giữ bản cục bộ, KHÔNG báo lỗi.
 * - Xem dưới quyền người khác: chỉ đọc, không ghi (backend cũng chặn).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import {
  loadChannelsPref, mergeChannelsPref, normalizeChannelIds, parseChannelsPref, saveChannelsPref, type ChannelsPref,
} from '@/lib/mobile-channels'

const URL = '/me/ui-prefs/mobile-channels'
export const CHANNELS_PUSH_DEBOUNCE_MS = 500

export function useMobileChannels(userId: string | undefined, opts: { readOnly?: boolean } = {}) {
  const { readOnly } = opts
  const qc = useQueryClient()
  const [pref, setPref] = useState<ChannelsPref | null>(() => loadChannelsPref(userId))
  const prefRef = useRef(pref)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dirty = useRef<ChannelsPref | null>(null)
  const queryKey = useMemo(() => ['ui-prefs', 'mobile-channels', userId] as const, [userId])

  const apply = useCallback((next: ChannelsPref | null) => { prefRef.current = next; setPref(next) }, [])

  // Đổi người dùng (đăng nhập lại, xem dưới quyền) → nạp bộ nhớ đệm của người đó.
  useEffect(() => { apply(loadChannelsPref(userId)) }, [userId, apply])

  const push = useCallback(async (p: ChannelsPref) => {
    if (readOnly || !userId) return
    try {
      const { data } = await api.put<{ pref: ChannelsPref; applied: boolean }>(URL, p)
      qc.setQueryData(queryKey, { pref: data.pref })
      // Máy chủ đang giữ bản mới hơn (thiết bị khác vừa sửa) → nhận bản đó.
      if (!data.applied) {
        const server = parseChannelsPref(data.pref)
        if (server) { saveChannelsPref(userId, server); apply(server) }
      }
    } catch { /* mạng lỗi / 403 → giữ bản cục bộ, lần hợp nhất sau tự thử lại */ }
  }, [readOnly, userId, qc, queryKey, apply])

  const flush = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    const p = dirty.current
    dirty.current = null
    if (p) void push(p)
  }, [push])

  const schedule = useCallback((p: ChannelsPref) => {
    dirty.current = p
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(flush, CHANNELS_PUSH_DEBOUNCE_MS)
  }, [flush])

  // Rời màn Hộp thư (vào hội thoại…) khi còn bản chờ gửi → gửi luôn.
  useEffect(() => () => flush(), [flush])

  const remote = useQuery<{ pref: ChannelsPref | null }>({
    queryKey,
    enabled: !!userId,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    queryFn: async () => {
      const { data } = await api.get<{ pref: unknown }>(URL)
      return { pref: parseChannelsPref(data.pref) }
    },
  })

  // Có bản máy chủ (kể cả "chưa có") → hợp nhất. Đọc bản cục bộ MỚI NHẤT qua ref (người dùng có thể vừa sửa trong lúc chờ mạng).
  useEffect(() => {
    if (!remote.data || !userId) return
    const { pref: merged, action } = mergeChannelsPref(prefRef.current, remote.data.pref)
    if (action === 'adopt-remote' && merged) { saveChannelsPref(userId, merged); apply(merged) }
    else if (action === 'push-local' && merged) schedule(merged)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote.dataUpdatedAt, userId])

  /** Người dùng xếp lại: áp ngay + lưu đệm + đẩy nền. Truyền `[]` = quay về thứ tự mặc định. */
  const setOrder = useCallback((channels: string[]) => {
    const next: ChannelsPref = { channels: normalizeChannelIds(channels), updatedAt: new Date().toISOString() }
    saveChannelsPref(userId, next)
    apply(next)
    schedule(next)
  }, [userId, apply, schedule])

  const order = useMemo(() => pref?.channels ?? [], [pref])
  return { order, setOrder }
}
